import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { db } from '../../lib/firebase.js';
import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { pricingPlans, freeTierLimits } from '../../lib/stripe.js';

function AdminSettings() {
  const { getToken } = useAuth();
  const [settings, setSettings] = useState({
    // Feature flags
    avatarGenerationEnabled: true,
    audioGenerationEnabled: true,
    imageGenerationEnabled: true,
    newUserSignupEnabled: true,
    maintenanceMode: false,
    // Limits
    maxStoriesPerDay: 100,
    maxImagesPerStory: 10,
    // API
    primaryTTSProvider: 'xai',
    fallbackTTSEnabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [systemHealth, setSystemHealth] = useState({});
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);

  useEffect(() => {
    loadSettings();
    checkSystemHealth();
  }, []);

  const loadSettings = async () => {
    try {
      const token = await getToken();
      const response = await fetch('/api/admin?view=settings', { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...(data.settings || {}) }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = await getToken();
      await fetch('/api/admin', { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'settings', settings }) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const checkSystemHealth = async () => {
    setCheckingHealth(true);
    const health = {
      firebase: { status: 'checking', message: 'Checking...' },
      api: { status: 'checking', message: 'Checking...' },
    };
    setSystemHealth(health);

    // Check Firebase
    try {
      await getDocs(collection(db, 'stories'));
      health.firebase = { status: 'healthy', message: 'Connected' };
    } catch (error) {
      health.firebase = { status: 'error', message: error.message };
    }

    // Check API (basic connectivity)
    try {
      const response = await fetch('/api/generate-story', {
        method: 'OPTIONS',
      });
      health.api = { status: 'healthy', message: 'Endpoints available' };
    } catch (error) {
      health.api = { status: 'warning', message: 'Could not verify' };
    }

    setSystemHealth(health);
    setCheckingHealth(false);
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'warning':
        return <InfoIcon sx={{ color: 'warning.main' }} />;
      default:
        return <InfoIcon sx={{ color: 'grey.400' }} />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          System Settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {saved && (
            <Alert severity="success" sx={{ py: 0 }}>
              Settings saved!
            </Alert>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Feature Flags */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Feature Flags
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Avatar Generation"
                  secondary="Allow users to generate character avatars with AI"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.avatarGenerationEnabled}
                    onChange={() => handleToggle('avatarGenerationEnabled')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Audio Generation"
                  secondary="Enable text-to-speech for stories"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.audioGenerationEnabled}
                    onChange={() => handleToggle('audioGenerationEnabled')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Image Generation"
                  secondary="Generate illustrations for story pages"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.imageGenerationEnabled}
                    onChange={() => handleToggle('imageGenerationEnabled')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="New User Signups"
                  secondary="Allow new users to create accounts"
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.newUserSignupEnabled}
                    onChange={() => handleToggle('newUserSignupEnabled')}
                  />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Maintenance Mode"
                  secondary="Show maintenance message to all users"
                  primaryTypographyProps={{
                    color: settings.maintenanceMode ? 'error' : 'inherit',
                  }}
                />
                <ListItemSecondaryAction>
                  <Switch
                    checked={settings.maintenanceMode}
                    onChange={() => handleToggle('maintenanceMode')}
                    color="error"
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* System Health */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                System Health
              </Typography>
              <IconButton onClick={checkSystemHealth} disabled={checkingHealth} size="small">
                <RefreshIcon />
              </IconButton>
            </Box>
            <List>
              <ListItem>
                <ListItemText
                  primary="Firebase Database"
                  secondary={systemHealth.firebase?.message || 'Not checked'}
                />
                <ListItemSecondaryAction>
                  {getStatusIcon(systemHealth.firebase?.status)}
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="API Endpoints"
                  secondary={systemHealth.api?.message || 'Not checked'}
                />
                <ListItemSecondaryAction>
                  {getStatusIcon(systemHealth.api?.status)}
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>

          {/* API Configuration */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              API Configuration
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Primary TTS Provider"
                  secondary="xAI expressive TTS (WAV narration)"
                />
                <ListItemSecondaryAction>
                  <Chip label="Active" color="success" size="small" />
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Image Generation Provider"
                  secondary="Google Gemini (Imagen 3 + Gemini Flash)"
                />
                <ListItemSecondaryAction>
                  <Chip label="Active" color="success" size="small" />
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Pricing Tiers */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Subscription Tiers & Limits
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPricingDialogOpen(true)}
              >
                View Full Details
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tier</TableCell>
                    <TableCell align="center">Monthly Price</TableCell>
                    <TableCell align="center">Yearly Price</TableCell>
                    <TableCell align="center">Stories/Month</TableCell>
                    <TableCell align="center">Characters</TableCell>
                    <TableCell align="center">Regenerations</TableCell>
                    <TableCell align="center">Audio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Free Tier */}
                  <TableRow>
                    <TableCell>
                      <Chip label="Free" size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">Free</TableCell>
                    <TableCell align="center">-</TableCell>
                    <TableCell align="center">{freeTierLimits.storiesPerMonth}</TableCell>
                    <TableCell align="center">{freeTierLimits.maxCharacters}</TableCell>
                    <TableCell align="center">{freeTierLimits.regenerationsPerStory}</TableCell>
                    <TableCell align="center">
                      <Chip label="No" size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                  {/* Paid Tiers */}
                  {pricingPlans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <Chip
                          label={plan.name}
                          size="small"
                          color={plan.id === 'basic' ? 'success' : plan.id === 'premium' ? 'warning' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell align="center">${plan.monthlyPrice}</TableCell>
                      <TableCell align="center">${plan.yearlyPrice}</TableCell>
                      <TableCell align="center">
                        {plan.limits.storiesPerMonth === Infinity ? 'Unlimited' : plan.limits.storiesPerMonth}
                      </TableCell>
                      <TableCell align="center">
                        {plan.limits.maxCharacters === Infinity ? 'Unlimited' : plan.limits.maxCharacters}
                      </TableCell>
                      <TableCell align="center">
                        {plan.limits.regenerationsPerStory === Infinity ? 'Unlimited' : plan.limits.regenerationsPerStory}
                      </TableCell>
                      <TableCell align="center">
                        {plan.limits.audioEnabled ? (
                          <Chip label="Yes" size="small" color="success" />
                        ) : (
                          <Chip label="No" size="small" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Environment Variables Status */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Environment Configuration
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Environment variables are configured at the server level. To modify them, update your
              Vercel project settings or local .env file.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      OpenAI API
                    </Typography>
                    <Chip
                      label="Configured"
                      size="small"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Gemini API
                    </Typography>
                    <Chip
                      label="Configured"
                      size="small"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Stripe API
                    </Typography>
                    <Chip
                      label="Configured"
                      size="small"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Clerk Auth
                    </Typography>
                    <Chip
                      label="Configured"
                      size="small"
                      color="success"
                      sx={{ mt: 0.5 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Pricing Details Dialog */}
      <Dialog
        open={pricingDialogOpen}
        onClose={() => setPricingDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Subscription Tier Details</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Free Tier */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Free</Typography>
                  <Typography variant="h4" color="primary" gutterBottom>Free</Typography>
                  <Divider sx={{ my: 2 }} />
                  <List dense>
                    <ListItem>
                      <ListItemText primary={`${freeTierLimits.storiesPerMonth} stories/month`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary={`${freeTierLimits.maxCharacters} characters`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary={`${freeTierLimits.regenerationsPerStory} regenerations/story`} />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="No audio narration" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            {/* Paid Tiers */}
            {pricingPlans.map((plan) => (
              <Grid item xs={12} md={6} key={plan.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="h4" color="primary" gutterBottom>
                      ${plan.monthlyPrice}/mo
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      or ${plan.yearlyPrice}/year (save ${(plan.monthlyPrice * 12 - plan.yearlyPrice).toFixed(0)})
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary={`${plan.limits.storiesPerMonth === Infinity ? 'Unlimited' : plan.limits.storiesPerMonth} stories/month`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={`${plan.limits.maxCharacters === Infinity ? 'Unlimited' : plan.limits.maxCharacters} characters`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={`${plan.limits.regenerationsPerStory === Infinity ? 'Unlimited' : plan.limits.regenerationsPerStory} regenerations/story`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary={plan.limits.audioEnabled ? 'Audio narration included' : 'No audio narration'}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminSettings;
